import { KPI_TO_API_KEY_MAP } from "../components/InternalConfigurationTable/hooks/useMarketConfig";

/**
 * Reverse of useMarketConfig's buildMarketConfig: turn one backend
 * formula-weights row (a single workspace + country, as returned by
 * GET /all_clients/formula_weights) into the UI state that the shared
 * Step2 (weights) and Step3 (assignments) components consume when editing.
 *
 * Weights are stored as decimals (summing to 1.0) and rendered as 0-100
 * percentages, so each is scaled x100 (rounded to the InputNumber's 1-decimal
 * precision). Core-brand items and competitors are mapped back to the
 * type-tagged shapes Step3 expects.
 *
 * @param {object} row - Backend row with decimal weight fields
 *   (market_penetration, ...), core_brands, companies, competitors.
 * @returns {{ weights: object, assignment: object }} weights keyed by the UI
 *   KPI keys (0-100); assignment as { company, coreBrandGroups, competitors }.
 */
export function prefillFromRow(row) {
  const src = row || {};

  const weights = {};
  Object.entries(KPI_TO_API_KEY_MAP).forEach(([uiKey, apiKey]) => {
    const decimal = Number(src[apiKey]);
    weights[uiKey] = Number.isFinite(decimal)
      ? parseFloat((decimal * 100).toFixed(1))
      : 0;
  });

  const coreBrandGroups = (src.core_brands || []).map((group, index) => ({
    id: `prefill_${index}`,
    name: group?.name || `core_brand_${index + 1}`,
    items: (group?.content || [])
      .filter((item) => item && item.brand)
      .map((item) =>
        item.subbrand
          ? { type: "subbrand", brand: item.brand, subbrand: item.subbrand }
          : { type: "brand", brand: item.brand },
      ),
  }));

  const companies = Array.isArray(src.companies) ? src.companies : [];
  const competitors = (src.competitors || [])
    .filter((c) => c && c.company)
    .map((c) =>
      c.brand
        ? { competitor: c.company, brand: c.brand }
        : { competitor: c.company },
    );

  return {
    weights,
    assignment: {
      company: companies[0] || null,
      coreBrandGroups,
      competitors,
    },
  };
}
